import { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Link } from 'foundations-components/transitional/components/Link';
import { Button } from 'foundations-components/transitional/components/Button';
import { useFetchTeamMembers } from '../../hooks/useFetchTeamMembers';
import { IconAvatar } from './IconAvatar';
import { SharingListItem } from './SharingListItem';
import { MemberItem } from './MemberItem';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_VISIBLE_MEMBERS = 9;
const MAX_ROWS_PER_COLUMN = 5;
const MembersGrid = styled.div.withConfig({
  displayName: "TeamMembersPopover__MembersGrid"
})(["display:grid;grid-template-rows:repeat(", ",auto);grid-auto-flow:column;column-gap:20px;"], MAX_ROWS_PER_COLUMN);
const PopoverContent = ({
  userIds
}) => {
  const limitedUserIds = useMemo(() => userIds.slice(0, MAX_VISIBLE_MEMBERS), [userIds]);
  const {
    data,
    loading
  } = useFetchTeamMembers(limitedUserIds);
  const members = useMemo(() => (data === null || data === void 0 ? void 0 : data.teamMembers) || [], [data === null || data === void 0 ? void 0 : data.teamMembers]);
  const teamsSettingsUrl = `/settings/${PortalIdParser.get()}/users/teams`;
  const remainingCount = userIds.length - MAX_VISIBLE_MEMBERS;
  if (loading) {
    return /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      blockSize: "100px",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        size: "small"
      })
    });
  }
  if (members.length === 0) {
    return /*#__PURE__*/_jsx(Text, {
      typography: "body-100",
      color: "text-core-subtle",
      paddingBlockEnd: "400",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.sharingPanel.TeamMembersPopover.noMembers"
      })
    });
  }
  const displayedMembers = members.slice(0, MAX_VISIBLE_MEMBERS);
  return /*#__PURE__*/_jsx(Box, {
    paddingBlockEnd: "400",
    children: /*#__PURE__*/_jsxs(MembersGrid, {
      children: [displayedMembers.map(user => /*#__PURE__*/_jsx(MemberItem, {
        user: user
      }, user.id)), remainingCount > 0 && /*#__PURE__*/_jsx(Link, {
        href: teamsSettingsUrl,
        external: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "viewsManagement.modals.manageSharing.sharingPanel.TeamMembersPopover.seeMoreMembers",
          options: {
            count: remainingCount
          }
        })
      })]
    })
  });
};
export const TeamMembersPopover = ({
  teamName,
  userIds
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleOpenChange = useCallback(evt => {
    setIsOpen(evt.currentTarget.value);
  }, []);
  const handleClick = useCallback(() => {
    setIsOpen(true);
  }, []);
  const teamInitial = teamName.charAt(0).toUpperCase();
  return /*#__PURE__*/_jsx(Popover, {
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement: "bottom",
    closeOnOutsideClick: true,
    content: {
      header: /*#__PURE__*/_jsx(Box, {
        maxInlineSize: "300px",
        children: /*#__PURE__*/_jsx(SharingListItem, {
          avatar: /*#__PURE__*/_jsx(IconAvatar, {
            children: teamInitial
          }),
          primaryText: teamName,
          secondaryText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.manageSharing.sharingPanel.teamMembersCount",
            options: {
              count: userIds.length
            }
          }),
          badge: null
        })
      }),
      body: /*#__PURE__*/_jsx(PopoverContent, {
        userIds: userIds
      })
    },
    children: /*#__PURE__*/_jsx(Button, {
      use: "link",
      onClick: handleClick,
      "data-test-id": "team-members-popover-trigger",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.modals.manageSharing.sharingPanel.teamMembersCount",
        options: {
          count: userIds.length
        }
      })
    })
  });
};